import { Location } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, ViewChild, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { MutationService } from '@app/core/graphql/mutation/mutation.service';
import { State } from '@app/core/ngrx/reducers';
import { applicationSelectors } from '@app/core/ngrx/selectors';
import { getState } from '@app/core/ngrx/utils/state';
import { ApplicationService } from '@app/core/services/application/application.service';
import { PaymentService } from '@app/core/services/payment/payment.service';
import { ModalService } from '@core/services/modal/modal.service';
import { WarningService } from '@core/services/warning/warning.service';
import { ApplicationActions } from '@core/ngrx/actions';
import { faClinicMedical, faUserMd } from '@fortawesome/free-solid-svg-icons';
import { IonSlides, NavController } from '@ionic/angular';

import { ToolService } from '@app/core/services/tool/tool.service';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { QueryService } from '@app/core/graphql/query/query.service';
import { reqDocsCheck } from '@app/shared/utils';
import { flatMap, switchMap } from 'rxjs/operators';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { GeneralDocumentsComponent } from '@app/modules/document/components/document-general/general-documents.component';
import { ApplicationSummaryComponent } from '@app/modules/application/components/application-program-summary/application-program-summary.component';
import { ApplicationPaymentActionComponent } from '../application-payment-action/application-payment-action.component';

@Component({
    selector: 'app-reserved',
    templateUrl: './application-reserved.component.html',
    styleUrls: ['./application-reserved.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        IonicModule,
        FontAwesomeModule,
        GeneralDocumentsComponent,
        ApplicationSummaryComponent,
        ApplicationPaymentActionComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ApplicationReservedComponent implements OnChanges, OnDestroy {
    @Input() status: any;
    @ViewChild('paymentSlider') slides: IonSlides;

    faClinicMedical = faClinicMedical;
    faUserMd = faUserMd;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private navCtrl: NavController,
        private location: Location,
        private mutationService: MutationService,
        private applicationService: ApplicationService,
        private paymentService: PaymentService,
        private store: Store<State>,
        private queryService: QueryService,
        private warning: WarningService,
        private modalService: ModalService,
        public toolService: ToolService
    ) {}

    malpractice$: Observable<boolean>;
    enrollment$: Observable<boolean>;
    checker$ = this.applicationService.enrollmentChecker();

    showMeTheMoney = this.paymentService.getShowMeTheMoney();
    ready = false;
    applicationid: string;
    userid: string;
    reserved: any = {
        view: 'document',
    };
    content: any;
    contentJson: any;
    requiredDocs: string;
    subscription: any;

    ngOnChanges() {
        // everytime this url changes reload the page
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.ready = true;
        this.enrollment$ = this.store.pipe(select(applicationSelectors.getEnroll)); // subscribe to canEnroll store value
        this.malpractice$ = this.store.pipe(select(applicationSelectors.getNeedMalpractice));
        this.listener(); // listen when visitor uploads a document by behaviorSubject.
    }

    listener = () => {
        this.checker$
            .pipe(
                flatMap((x: boolean) => {
                    return this.queryService.enrollmentCheck(this.status.application.id);
                })
            )
            .subscribe((response: any) => {
                const canEnroll = response.data.user.application.canEnroll;
                // update store with canEnroll check
                this.store.dispatch(ApplicationActions.setCanEnroll({ canEnroll }));
            });
    };

    ngOnDestroy() {
        // this.subscription.unsubscribe();
    }

    requestInvitationLetter = () => {
        this.applicationService.requestInvitation(this.applicationid).subscribe((response: any) => {
            this.reserved.currentState.requiresinvitationletter = false;
            // eslint-disable-next-line max-len
            // this.alertService.basicAlert('Success', 'Invitation Letter', 'Your have successfully requested you invitation letter.', ['Dismiss']);
        });
    };

    malpracticePayment = () => {
        const navigationExtras: NavigationExtras = {
            queryParams: {
                paymenttype: 'malpractice',
                isinsurance: true,
                userid: this.userid,
            },
        };
        this.navCtrl.navigateRoot([`/payment/${this.applicationid}`], navigationExtras);
    };

    requestEnrollment = () => {
        if (!getState(this.store)['application'].canEnroll) {
            this.mutationService.enrollmentChecker(this.status.application.id).subscribe((res: any) => {
                // console.log(res);
                this.modalService.enrollmentChecklist(res.data.enrollmentChecker.missing, res.data.enrollmentChecker.complete);
            });
            // const missing = enrollmentMissingRequirements(
            //   this.status.application,
            //   this.status.user,
            //   this.status.requiredProgramDocuments.documents,
            //   this.status.submittedProgramDocuments.documents,
            //   this.status.immunizations.documents,
            //   this.status.applicationDocuments.documents
            //   );
            // this.warning.create(`${missing[0]}`);
        } else if (this.status.application.enrollable) {
            this.warning.create('This application has already requested enrollment.');
        } else {
            this.applicationService.requestEnrollment(this.status.application.id).subscribe((response: any) => {
                if (response.type === 'success') {
                    this.modalService.successfulAction({
                        message: 'Congratulations, you have successfully requested enrollment!',
                    });
                    this.status.application = { ...this.status.application, enrollable: true };
                } else {
                    this.warning.create('Enrollment request was not received. Please try again later.');
                }
            });
        }
    };

    nextSlide = (step: string) => {
        this.slides.lockSwipeToPrev(true);
        if (step === 'summary') {
            const url = this.router
                .createUrlTree([`/application/${this.status.application.id}`], {
                    relativeTo: this.route,
                    queryParams: {
                        view: 'payment',
                        step: 'payment-summary',
                    },
                })
                .toString();
            this.location.replaceState(url);
            this.slides.slideNext();
        } else if (step === 'details') {
            const url = this.router
                .createUrlTree([`/application/${this.status.application.id}`], {
                    relativeTo: this.route,
                    queryParams: {
                        view: 'payment',
                        step: 'payment-details',
                    },
                })
                .toString();
            this.location.replaceState(url);
            this.slides.slideNext();
        } else {
            const url = this.router.createUrlTree([`/application/${this.status.application.id}`]).toString();
            this.location.replaceState(url);
        }
    };

    segmentChanged(event: any) {
        this.reserved.view = event.detail.value;
    }
}
